import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  limitReviews,
  policyholderReviews,
  settings,
  type Policyholder,
  type PolicyholderReview,
  type PolicyholderReviewDecision,
  type PolicyholderReviewDocument,
  type PolicyholderReviewKind,
  type PolicyholderReviewSetting,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatDateTime,
  formatMoney,
  moneyInputError,
  moneyToInput,
  parseMoney,
} from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../../shared/fieldError';
import { gruposDeModalidade } from '../rotulos';
import { Analises } from './Secoes';
import { Bloco, Secao } from './shared';

/**
 * Secao Analises da ficha do tomador (P-TOM-C4), na ordem do OnPoint: primeiro o
 * processo de analise do proprio tomador (cadastro, credito, PEP, grupo economico e
 * revisao de limite), com a linha do tempo e os documentos pedidos; depois as analises
 * de credito das operacoes, que o SeguraCore ja mostrava. Ver
 * docs/ADR/0058-analises-do-tomador.md.
 *
 * A decisao e de quem tem a alcada e so registra: a tela diz que nada muda no limite,
 * no cadastro nem no bloqueio do tomador. A corretora le as analises dos tomadores
 * dela, sem as notas internas.
 */
export function SecaoAnalises({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  return (
    <div className="flex flex-col gap-6">
      {can('tomadores.analise.view') ? <AnalisesDoTomador tomador={tomador} /> : null}
      <Analises tomador={tomador} />
    </div>
  );
}

const rotulosDoTipoDeAnalise: Record<PolicyholderReviewKind, string> = {
  cadastro: 'Analise de cadastro',
  credito: 'Analise de credito',
  pep: 'Analise PEP',
  grupo_economico: 'Definicao de grupo economico',
  revisao_limite: 'Revisao de limite',
};

const rotulosDoRegistro: Record<string, string> = {
  abertura: 'Analise aberta',
  anotacao: 'Anotacao',
  alteracao: 'Motivo alterado',
  cartao: 'Cartao da esteira',
  decisao: 'Decisao',
  cancelamento: 'Cancelamento',
};

const rotulosDaDecisao: Record<PolicyholderReviewDecision, string> = {
  aprovar: 'Aprovar',
  recusar: 'Recusar',
  cancelar: 'Cancelar a analise',
};

function erroDaApi(erro: Error | null): ApiError | null {
  return erro instanceof ApiError ? erro : null;
}

function rotuloDoTipo(kind: string): string {
  return rotulosDoTipoDeAnalise[kind as PolicyholderReviewKind] ?? kind;
}

export function AnalisesDoTomador({ tomador }: { tomador: Policyholder }) {
  const { can } = useAuth();
  const [abrindo, setAbrindo] = useState(false);
  const [anotando, setAnotando] = useState<PolicyholderReview | null>(null);
  const [decidindo, setDecidindo] = useState<PolicyholderReview | null>(null);
  const [linhaDoTempo, setLinhaDoTempo] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['policyholder', tomador.id, 'reviews'],
    queryFn: ({ signal }) =>
      policyholderReviews.list(tomador.id, { limit: 50 }, signal),
  });

  const meta = consulta.data?.meta;
  const itens = consulta.data?.items ?? [];
  const podeAnotar = can('tomadores.analise.create');
  const podeDecidir = can('tomadores.analise.approve');
  // A analise de revisao de limite decide o pedido pelo servico dele, que pede tambem
  // as permissoes do pedido: sem elas, o servidor responde 403 (B45).
  const alcadaDe = (a: PolicyholderReview) => {
    const revisao = a.kind === 'revisao_limite';
    const lePedido = !revisao || can('credito.revisao_limite.view');
    return {
      decidir:
        podeDecidir && lePedido && (!revisao || can('credito.revisao_limite.approve')),
      cancelar:
        podeAnotar && lePedido && (!revisao || can('credito.revisao_limite.create')),
    };
  };
  const alcadaDaDecisao = decidindo
    ? alcadaDe(decidindo)
    : { decidir: false, cancelar: false };
  const podeAbrir =
    meta?.can_open === true && podeAnotar && (meta.open_kinds.length ?? 0) > 0;
  const aberta = itens.find((item) => item.id === linhaDoTempo) ?? null;

  return (
    <Secao
      titulo="Analises do tomador"
      descricao="Processo de analise do proprio tomador, fora da operacao: cadastro, credito, PEP, grupo economico e revisao de limite, com o cartao da esteira e a linha do tempo."
      carregando={consulta.isPending}
      erro={consulta.error}
      acao={
        podeAbrir ? (
          <Button size="sm" onClick={() => setAbrindo(true)}>
            Abrir analise
          </Button>
        ) : null
      }
    >
      <div className="flex flex-col gap-5">
        {meta ? <Indicadores meta={meta} /> : null}
        <Alert tone="info" title="Decisao manual">
          Quem tem a alcada aprova ou recusa a analise, e a decisao fica registrada com
          quem, o que e quando. Ela nao muda cadastro nem bloqueio do tomador, e o
          limite so muda na revisao de limite aprovada: criterios e alcadas por tipo
          ainda dependem de regra da seguradora
          {meta && meta.pending_rules.length > 0
            ? ` (${meta.pending_rules.join(', ')})`
            : ''}
          .
        </Alert>
        <DataTable
          columns={[
            {
              key: 'tipo',
              header: 'Tipo',
              render: (a: PolicyholderReview) => a.kind_label || rotuloDoTipo(a.kind),
            },
            {
              key: 'situacao',
              header: 'Situacao',
              render: (a) => <StatusBadge kind="pedidoCartao" value={a.status} />,
            },
            {
              key: 'aberta',
              header: 'Aberta em',
              render: (a) => formatDateTime(a.opened_at),
            },
            {
              key: 'quem',
              header: 'Aberta por',
              hideBelow: 'md',
              render: (a) => a.opened_label || '-',
            },
            {
              key: 'esteira',
              header: 'Esteira',
              hideBelow: 'md',
              render: (a) => <OndeCorre analise={a} />,
            },
            {
              key: 'decisao',
              header: 'Decisao',
              render: (a) =>
                a.decided_at ? (
                  <span className="flex flex-col">
                    <span>
                      {a.decided_label || '-'} em {formatDateTime(a.decided_at)}
                    </span>
                    {a.decided_by_engine ? (
                      <Badge tone="info">Aprovacao automatica do motor</Badge>
                    ) : null}
                    {a.decline_reason ? (
                      <span className="text-2xs text-ink-500">{a.decline_reason}</span>
                    ) : null}
                  </span>
                ) : (
                  '-'
                ),
            },
            {
              key: 'acoes',
              header: '',
              render: (a) => (
                <span className="flex gap-1">
                  <AcaoComIcone
                    acao="historico"
                    rotulo={
                      linhaDoTempo === a.id ? 'Fechar linha do tempo' : 'Linha do tempo'
                    }
                    onClick={() => setLinhaDoTempo(linhaDoTempo === a.id ? null : a.id)}
                  />
                  {podeAnotar && !a.redacted ? (
                    <AcaoComIcone
                      acao="anotar"
                      rotulo="Anotar"
                      onClick={() => setAnotando(a)}
                    />
                  ) : null}
                  {a.status === 'pendente' &&
                  !a.redacted &&
                  (alcadaDe(a).decidir || alcadaDe(a).cancelar) ? (
                    <AcaoComIcone
                      acao="decidir"
                      rotulo="Decidir"
                      onClick={() => setDecidindo(a)}
                    />
                  ) : null}
                </span>
              ),
            },
          ]}
          items={itens}
          keyOf={(a) => a.id}
          empty={
            <EmptyState
              icon="coins"
              title="Ops! Nenhuma analise encontrada para este tomador."
              description="A analise do tomador corre fora da operacao. As analises de credito das operacoes aparecem logo abaixo."
            />
          }
        />
        {aberta ? <LinhaDoTempo tomadorId={tomador.id} analise={aberta} /> : null}
        {meta ? <DocumentosPedidos meta={meta} /> : null}
        {can('esteiras.configuracao.update') ? <ConfiguracaoDasAnalises /> : null}
      </div>
      <AbrirAnalise
        tomadorId={tomador.id}
        open={abrindo}
        tipos={meta?.open_kinds ?? []}
        onClose={() => setAbrindo(false)}
      />
      <AnotarAnalise
        tomadorId={tomador.id}
        analise={anotando}
        onClose={() => setAnotando(null)}
      />
      <DecidirAnalise
        tomadorId={tomador.id}
        analise={decidindo}
        podeDecidir={alcadaDaDecisao.decidir}
        podeCancelar={alcadaDaDecisao.cancelar}
        onClose={() => setDecidindo(null)}
      />
    </Secao>
  );
}

function Indicadores({
  meta,
}: {
  meta: {
    has_card_in_analysis: boolean;
    credit_review_in_progress: boolean;
    economic_group_review_in_progress: boolean;
    pep_review_approved: boolean;
    has_pending_documents: boolean;
  };
}) {
  return (
    <div className="flex flex-wrap gap-2" aria-label="Indicadores das analises">
      <Badge tone={meta.has_card_in_analysis ? 'warning' : 'neutral'}>
        {meta.has_card_in_analysis
          ? 'Ha analise em andamento'
          : 'Nenhuma analise em andamento'}
      </Badge>
      {meta.credit_review_in_progress ? (
        <Badge tone="warning">Credito em analise</Badge>
      ) : null}
      {meta.economic_group_review_in_progress ? (
        <Badge tone="warning">Grupo economico em definicao</Badge>
      ) : null}
      {meta.pep_review_approved ? <Badge tone="success">PEP aprovado</Badge> : null}
      {meta.has_pending_documents ? (
        <Badge tone="danger">Documento obrigatorio pendente</Badge>
      ) : null}
    </div>
  );
}

function OndeCorre({ analise }: { analise: PolicyholderReview }) {
  if (analise.kind === 'revisao_limite' && analise.limit_review) {
    const pedido = analise.limit_review;
    return (
      <span className="flex flex-col">
        <span>
          Pedido de revisao:{' '}
          {gruposDeModalidade[pedido.modality_group] ?? pedido.modality_group}
          {pedido.requested_amount ? ` - ${formatMoney(pedido.requested_amount)}` : ''}
        </span>
        {analise.card ? (
          <span className="text-2xs text-ink-500">
            {analise.card.pipe_name} - {analise.card.step_name}
          </span>
        ) : null}
      </span>
    );
  }
  if (!analise.card) {
    return (
      <span className="text-ink-500">{analise.redacted ? '-' : 'Sem cartao'}</span>
    );
  }
  return (
    <span className="flex flex-col">
      <span>
        {analise.card.pipe_name} - {analise.card.step_name}
      </span>
      <span className="text-2xs text-ink-500">
        {analise.card.opened
          ? 'Cartao aberto pela analise'
          : 'Cartao que ja estava aberto'}
        {analise.card.is_closed ? ', encerrado' : ''}
      </span>
    </span>
  );
}

function LinhaDoTempo({
  tomadorId,
  analise,
}: {
  tomadorId: string;
  analise: PolicyholderReview;
}) {
  const eventos = useQuery({
    queryKey: ['policyholder', tomadorId, 'reviews', analise.id, 'events'],
    queryFn: ({ signal }) => policyholderReviews.events(tomadorId, analise.id, signal),
  });
  return (
    <Bloco
      titulo={`Linha do tempo: ${analise.kind_label || rotuloDoTipo(analise.kind)}`}
    >
      {eventos.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a linha do tempo">
          {eventos.error.message}
        </Alert>
      ) : (
        <ol className="flex flex-col gap-3" aria-label="Linha do tempo da analise">
          {(eventos.data ?? []).map((e) => (
            <li key={e.id} className="rounded-md border border-ink-200 p-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-ink-900">
                  {rotulosDoRegistro[e.kind] ?? e.kind}
                </span>
                <StatusBadge kind="pedidoCartao" value={e.status} />
                {e.broker_visible ? (
                  <Badge tone="brand">Visivel a corretora</Badge>
                ) : null}
              </div>
              {e.note ? (
                <p className="mt-1 whitespace-pre-line text-ink-700">{e.note}</p>
              ) : null}
              {e.kind === 'cartao' && typeof e.detail['pipe'] === 'string' ? (
                <p className="mt-1 text-ink-700">
                  {e.detail['pipe']} -{' '}
                  {typeof e.detail['step'] === 'string' ? e.detail['step'] : ''}
                  {e.detail['opened'] === true
                    ? ' (aberto pela analise)'
                    : ' (ja estava aberto)'}
                </p>
              ) : null}
              <p className="mt-1 text-2xs text-ink-500">
                {e.actor_label || 'Sistema'} em {formatDateTime(e.created_at)}
              </p>
            </li>
          ))}
          {eventos.isPending ? (
            <li className="text-sm text-ink-500">Carregando...</li>
          ) : null}
        </ol>
      )}
    </Bloco>
  );
}

function DocumentosPedidos({
  meta,
}: {
  meta: {
    has_pending_documents: boolean;
    request_documents: PolicyholderReviewDocument[];
  };
}) {
  return (
    <Bloco titulo="Documentos pedidos nas analises">
      {meta.request_documents.length === 0 ? (
        <p className="text-sm text-ink-500">
          Nenhum documento pendente pedido nas analises em andamento.
        </p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm">
          {meta.request_documents.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-2">
              <span className="text-ink-900">{d.title}</span>
              {d.required ? <Badge tone="warning">Obrigatorio</Badge> : null}
              <Badge tone="neutral">Pendente</Badge>
            </li>
          ))}
        </ul>
      )}
      {meta.has_pending_documents ? (
        <p className="text-2xs text-ink-500">
          O tomador tem documento obrigatorio pendente de envio. O envio e feito na
          secao Documentos.
        </p>
      ) : null}
    </Bloco>
  );
}

// --- Abrir ---------------------------------------------------------------------------

function AbrirAnalise({
  tomadorId,
  open,
  tipos,
  onClose,
}: {
  tomadorId: string;
  open: boolean;
  tipos: PolicyholderReviewKind[];
  onClose: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tipo, setTipo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [pedido, setPedido] = useState('');
  const revisao = tipo === 'revisao_limite';

  const pedidos = useQuery({
    queryKey: ['limit-reviews', tomadorId, 'pendente'],
    queryFn: ({ signal }) =>
      limitReviews.list(
        { policyholder_id: tomadorId, status: 'pendente', limit: 50 },
        signal,
      ),
    enabled: open && revisao && can('credito.revisao_limite.view'),
  });

  const abrir = useMutation({
    mutationFn: () =>
      policyholderReviews.open(tomadorId, {
        kind: tipo as PolicyholderReviewKind,
        ...(motivo ? { reason: motivo } : {}),
        ...(revisao && pedido ? { limit_review_id: pedido } : {}),
      }),
    onSuccess: () => {
      toast.success('Analise aberta.');
      setTipo('');
      setMotivo('');
      setPedido('');
      void queryClient.invalidateQueries({
        queryKey: ['policyholder', tomadorId, 'reviews'],
      });
      onClose();
    },
  });
  const apiError = erroDaApi(abrir.error);
  const opcoesDePedido = (pedidos.data?.items ?? []).map((p) => ({
    value: p.id,
    label: `${gruposDeModalidade[p.modality_group] ?? p.modality_group} - ${formatMoney(p.requested_amount)}`,
  }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Abrir analise do tomador"
      description="A analise abre o cartao de tomador na esteira do assunto, ou aponta para o cartao que ja esta aberto."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={abrir.isPending}
            disabled={tipo === '' || (revisao && pedido === '')}
            onClick={() => abrir.mutate()}
          >
            Abrir analise
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {abrir.error ? (
          <Alert tone="danger" title="Nao foi possivel abrir a analise">
            {abrir.error.message}
          </Alert>
        ) : null}
        <Select
          label="Tipo da analise"
          value={tipo}
          placeholder="Escolha o tipo"
          options={tipos.map((k) => ({ value: k, label: rotuloDoTipo(k) }))}
          error={fieldErrorOf(apiError, 'kind')}
          onChange={(event) => setTipo(event.target.value)}
        />
        {revisao ? (
          <Select
            label="Pedido de revisao de limite"
            value={pedido}
            placeholder="Escolha o pedido pendente"
            options={opcoesDePedido}
            hint={
              opcoesDePedido.length === 0
                ? 'Nenhum pedido pendente. Peca a revisao na secao Revisao de Limite.'
                : 'A analise acompanha o pedido: a decisao dela e a do pedido.'
            }
            error={fieldErrorOf(apiError, 'limit_review_id')}
            onChange={(event) => setPedido(event.target.value)}
          />
        ) : null}
        <Textarea
          label="Motivo da analise"
          value={motivo}
          error={fieldErrorOf(apiError, 'reason')}
          onChange={(event) => setMotivo(event.target.value)}
        />
      </div>
    </Modal>
  );
}

// --- Anotar --------------------------------------------------------------------------

function AnotarAnalise({
  tomadorId,
  analise,
  onClose,
}: {
  tomadorId: string;
  analise: PolicyholderReview | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [nota, setNota] = useState('');
  const [paraCorretora, setParaCorretora] = useState(false);
  const [motivo, setMotivo] = useState<string | null>(null);
  const pendente = analise?.status === 'pendente';
  const motivoAtual = motivo ?? analise?.reason ?? '';

  const fechar = () => {
    setNota('');
    setParaCorretora(false);
    setMotivo(null);
    onClose();
  };

  const anotar = useMutation({
    mutationFn: () =>
      policyholderReviews.update(tomadorId, analise?.id ?? '', {
        ...(nota ? { note: nota, note_visible_to_broker: paraCorretora } : {}),
        ...(pendente && motivo !== null && motivo !== analise?.reason
          ? { reason: motivo }
          : {}),
      }),
    onSuccess: () => {
      toast.success('Registro gravado na analise.');
      void queryClient.invalidateQueries({
        queryKey: ['policyholder', tomadorId, 'reviews'],
      });
      fechar();
    },
  });
  const apiError = erroDaApi(anotar.error);

  return (
    <Modal
      open={analise !== null}
      onClose={fechar}
      title="Registrar na analise"
      description={
        analise ? analise.kind_label || rotuloDoTipo(analise.kind) : undefined
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Cancelar
          </Button>
          <Button loading={anotar.isPending} onClick={() => anotar.mutate()}>
            Gravar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {anotar.error ? (
          <Alert tone="danger" title="Nao foi possivel gravar">
            {anotar.error.message}
          </Alert>
        ) : null}
        <Textarea
          label="Anotacao"
          value={nota}
          error={fieldErrorOf(apiError, 'note')}
          onChange={(event) => setNota(event.target.value)}
        />
        <label className="flex items-center gap-2 text-xs font-semibold text-ink-700">
          <input
            type="checkbox"
            className="size-4"
            checked={paraCorretora && nota.trim() !== ''}
            disabled={nota.trim() === ''}
            onChange={(event) => setParaCorretora(event.target.checked)}
          />
          Mostrar a anotacao a corretora
        </label>
        {pendente ? (
          <Textarea
            label="Motivo da analise"
            value={motivoAtual}
            error={fieldErrorOf(apiError, 'reason')}
            onChange={(event) => setMotivo(event.target.value)}
          />
        ) : null}
      </div>
    </Modal>
  );
}

// --- Decidir -------------------------------------------------------------------------

function escopoDoMotivo(kind: PolicyholderReviewKind): string {
  if (kind === 'cadastro' || kind === 'grupo_economico') return 'cadastro';
  if (kind === 'pep') return 'pep';
  return 'credito';
}

function DecidirAnalise({
  tomadorId,
  analise,
  podeDecidir,
  podeCancelar,
  onClose,
}: {
  tomadorId: string;
  analise: PolicyholderReview | null;
  podeDecidir: boolean;
  podeCancelar: boolean;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [decisao, setDecisao] = useState('');
  const [motivo, setMotivo] = useState('');
  const [nota, setNota] = useState('');
  const [paraCorretora, setParaCorretora] = useState(false);
  const [limite, setLimite] = useState('');
  const recusa = decisao === 'recusar';
  const revisao = analise?.kind === 'revisao_limite';
  // O valor so aparece para quem tem a alcada do limite. Quem so decide o pedido conclui
  // sem mudar o limite.
  const pedeOLimite =
    revisao && decisao === 'aprovar' && can('tomadores.credito.update');
  const limiteAprovado = pedeOLimite ? parseMoney(limite) : null;

  const opcoes: PolicyholderReviewDecision[] = [
    ...(podeDecidir ? (['aprovar', 'recusar'] as const) : []),
    ...(podeCancelar ? (['cancelar'] as const) : []),
  ];

  const motivos = useQuery({
    queryKey: ['decline-reasons', analise ? escopoDoMotivo(analise.kind) : ''],
    queryFn: ({ signal }) =>
      settings.declineReasons(
        { scope: escopoDoMotivo(analise?.kind ?? 'credito') },
        signal,
      ),
    enabled: recusa && analise !== null,
    staleTime: 5 * 60_000,
  });

  const fechar = () => {
    setDecisao('');
    setMotivo('');
    setNota('');
    setParaCorretora(false);
    setLimite('');
    onClose();
  };

  const decidir = useMutation({
    mutationFn: () =>
      policyholderReviews.update(tomadorId, analise?.id ?? '', {
        decision: decisao as PolicyholderReviewDecision,
        ...(recusa && motivo ? { decline_reason_id: motivo } : {}),
        ...(nota ? { note: nota } : {}),
        // A marca so vale com a justificativa: sem texto, o servidor recusa (422).
        ...(paraCorretora && nota.trim() !== '' && !revisao
          ? { note_visible_to_broker: true }
          : {}),
        ...(limiteAprovado ? { approved_limit: limiteAprovado } : {}),
      }),
    onSuccess: () => {
      toast.success('Decisao registrada.');
      void queryClient.invalidateQueries({
        queryKey: ['policyholder', tomadorId, 'reviews'],
      });
      void queryClient.invalidateQueries({ queryKey: ['limit-reviews'] });
      void queryClient.invalidateQueries({ queryKey: ['policyholder', tomadorId] });
      fechar();
    },
  });
  const apiError = erroDaApi(decidir.error);

  return (
    <Modal
      open={analise !== null}
      onClose={fechar}
      title="Decidir a analise"
      description={
        analise ? analise.kind_label || rotuloDoTipo(analise.kind) : undefined
      }
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={fechar}>
            Voltar
          </Button>
          <Button
            variant={decisao === 'aprovar' ? 'primary' : 'danger'}
            loading={decidir.isPending}
            disabled={decisao === '' || (pedeOLimite && limiteAprovado === null)}
            onClick={() => decidir.mutate()}
          >
            Registrar decisao
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {decidir.error ? (
          <Alert tone="danger" title="Nao foi possivel decidir">
            {decidir.error.message}
          </Alert>
        ) : null}
        <Alert tone="info" title="O que a decisao faz">
          {revisao
            ? 'A decisao e a do pedido de revisao de limite: aprovar conclui o pedido, recusar recusa e cancelar cancela. O limite aprovado vai para Limites e Taxas, com historico. Sem a alcada do credito, aprovar conclui sem mudar o limite.'
            : 'A decisao fica registrada com quem, o que e quando, e leva o cartao para a coluna final quando nenhuma outra analise corre nele. Nao muda limite, cadastro nem bloqueio do tomador.'}
        </Alert>
        <Select
          label="Decisao da analise"
          value={decisao}
          placeholder="Escolha a decisao"
          options={opcoes.map((d) => ({ value: d, label: rotulosDaDecisao[d] }))}
          error={fieldErrorOf(apiError, 'decision')}
          onChange={(event) => {
            setDecisao(event.target.value);
            setLimite(moneyToInput(analise?.limit_review?.requested_amount));
          }}
        />
        {pedeOLimite ? (
          <Field
            label="Limite aprovado (R$)"
            inputMode="decimal"
            required
            value={limite}
            error={moneyInputError(limite) ?? fieldErrorOf(apiError, 'approved_limit')}
            hint="Comeca pelo limite pretendido. Fica em Limites e Taxas, como aprovado por voce."
            onChange={(event) => setLimite(event.target.value)}
          />
        ) : null}
        {recusa ? (
          <Select
            label="Motivo padronizado da recusa"
            value={motivo}
            placeholder="Escolha o motivo padronizado"
            options={(motivos.data ?? [])
              .filter((item) => item.active)
              .map((item) => ({ value: item.id, label: item.label }))}
            hint="Motivos da esteira do assunto, cadastrados em Configuracoes. Sem motivo, escreva a justificativa."
            error={fieldErrorOf(apiError, 'decline_reason_id')}
            onChange={(event) => setMotivo(event.target.value)}
          />
        ) : null}
        <Textarea
          label="Justificativa da decisao"
          value={nota}
          error={fieldErrorOf(apiError, 'note')}
          onChange={(event) => setNota(event.target.value)}
        />
        {!revisao ? (
          <label className="flex items-center gap-2 text-xs font-semibold text-ink-700">
            <input
              type="checkbox"
              className="size-4"
              checked={paraCorretora && nota.trim() !== ''}
              disabled={nota.trim() === ''}
              onChange={(event) => setParaCorretora(event.target.checked)}
            />
            Mostrar a justificativa a corretora
          </label>
        ) : null}
      </div>
    </Modal>
  );
}

// --- Configuracao ----------------------------------------------------------------------

/**
 * Configuracao do processo por empresa (quem configura as esteiras): o tipo ligado ou
 * desligado e a esteira do cartao de tomador. Sem escolha gravada, vale o OnPoint.
 */
function ConfiguracaoDasAnalises() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [rascunho, setRascunho] = useState<PolicyholderReviewSetting[] | null>(null);

  const consulta = useQuery({
    queryKey: ['policyholder-review-settings'],
    queryFn: ({ signal }) => policyholderReviews.settings(signal),
  });
  const itens = rascunho ?? consulta.data?.items ?? [];
  const esteiras = consulta.data?.pipes ?? [];

  const gravar = useMutation({
    mutationFn: () =>
      policyholderReviews.saveSettings(
        itens.map((item) => ({
          kind: item.kind,
          enabled: item.enabled,
          pipe_key: item.kind === 'revisao_limite' ? null : item.pipe_key,
        })),
      ),
    onSuccess: (dados) => {
      toast.success('Configuracao das analises gravada.');
      queryClient.setQueryData(['policyholder-review-settings'], dados);
      setRascunho(null);
      void queryClient.invalidateQueries({ queryKey: ['policyholder'] });
    },
  });
  const apiError = erroDaApi(gravar.error);

  const muda = (
    kind: PolicyholderReviewKind,
    parcial: Partial<PolicyholderReviewSetting>,
  ) =>
    setRascunho(
      itens.map((item) => (item.kind === kind ? { ...item, ...parcial } : item)),
    );

  return (
    <Bloco
      titulo="Configuracao das analises do tomador"
      acao={
        <Button
          size="sm"
          variant="secondary"
          loading={gravar.isPending}
          disabled={rascunho === null}
          onClick={() => gravar.mutate()}
        >
          Gravar configuracao
        </Button>
      }
    >
      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a configuracao">
          {consulta.error.message}
        </Alert>
      ) : null}
      {gravar.error ? (
        <Alert tone="danger" title="Nao foi possivel gravar">
          {gravar.error.message}
        </Alert>
      ) : null}
      <p className="text-2xs text-ink-500">
        Sem escolha gravada, vale o padrao: todos os tipos ligados, cada um na esteira
        do assunto. A revisao de limite corre no cartao do proprio pedido.
      </p>
      <ul className="flex flex-col gap-3">
        {itens.map((item) => (
          <li
            key={item.kind}
            className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-end"
          >
            <span className="text-sm font-semibold text-ink-900">
              {item.kind_label || rotuloDoTipo(item.kind)}
            </span>
            <label className="flex items-center gap-2 text-xs font-semibold text-ink-700">
              <input
                type="checkbox"
                className="size-4"
                checked={item.enabled}
                onChange={(event) => muda(item.kind, { enabled: event.target.checked })}
              />
              Ligada
            </label>
            {item.kind === 'revisao_limite' ? (
              <span className="text-2xs text-ink-500">
                Cartao do pedido, na esteira Credito
              </span>
            ) : (
              <Select
                label="Esteira do cartao"
                labelHidden
                value={item.pipe_key ?? ''}
                options={[
                  { value: '', label: 'Sem cartao' },
                  ...esteiras.map((p) => ({ value: p.key, label: p.name })),
                ]}
                error={fieldErrorOf(apiError, 'pipe_key')}
                onChange={(event) =>
                  muda(item.kind, {
                    pipe_key: event.target.value === '' ? null : event.target.value,
                  })
                }
              />
            )}
          </li>
        ))}
      </ul>
    </Bloco>
  );
}
